import React from "react";
import { FiArrowUpRight, FiMapPin, FiPhone, FiMail, FiCalendar, FiUser, FiShield } from "react-icons/fi";
import {
  formatDateIntoDMYToDisplay,
  selectedTabState,
} from "../../../Utils/utils";
import { useNavigate } from "react-router";
import useCareLabels from "hooks/useCareLabels";
import CareLabelSkeleton from "../CareLabelSkeleton/CareLabelSkeleton";
import CircularLoader from "../CircularLoader/CircularLoader";
import { USER_ROLE } from "../../../Constants/constant";
import { roleAccess } from "../../../helpers/RolesPermission";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
/** API may store plan_managed_by as a plain enum (e.g. "selfManaged") or JSON (e.g. [{"value":"planManaged"}]). */
const isPlanManagedByPlanManager = (raw) => {
  if (raw == null || raw === "") {
    return false;
  }
  const str = String(raw).trim();
  if (str === "planManaged") {
    return true;
  }
  if (!str.startsWith("[") && !str.startsWith("{")) {
    return false;
  }
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      return parsed[0]?.value === "planManaged";
    }
    if (parsed && typeof parsed === "object" && parsed.value === "planManaged") {
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

const formatAddress = (data) => {
  if (!data) return null;

  const street = data.street_no || data.address;
  const suburb = data.suburb || data.city;
  const state = data.state;
  const postcode = data.post_code || data.postcode || data.postal_code;
  const statePostcode = [state, postcode].filter(Boolean).join(" ");
  const parts = [street, suburb, statePostcode].filter(Boolean);

  return parts.length ? parts.join(", ") : null;
};

const QuickSnapshots = ({
  settingsData,
  participantsInfo,
  setToggleState,
  cssClass,
  isQuickViewCall,
  isLoading,
  paddingLeft,
  worker = false,
}) => {
  const navigate = useNavigate();
  const { participantLabel, isLoading: isCareLabelsLoading } = useCareLabels();

  const handleTabs = () => {
    setToggleState
      ? setToggleState({
          name: "Settings",
          id: 13,
          children: [],
        })
      : selectedTabState.save(
          JSON.stringify({
            name: "Settings",
            id: 13,
            children: [],
          })
        );

    worker
      ? navigate(`/worker-profile/${participantsInfo.uuid}`, {
          state: {
            id: 3,
          },
        })
      : navigate(`/sp-profile/${participantsInfo.uuid}`);
  };

  // For worker portal, info boxes should not be clickable only when the logged-in user is a worker.
  // When an SP/manager views a worker's quick view (worker=true), buttons should still work.
  const isWorkerUser = roleAccess([USER_ROLE.Sp_Standard_User]);
  const onClickHandler = isWorkerUser ? undefined : handleTabs;

  const getValue = (fieldName = "") => {
    const hasPermission = roleAccess(
      [],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.SETTING.SHOW
    ) &&
    roleAccess(
      [],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.DETAIL
    );
    
    // "Add information" is not clickable only when the logged-in user is a worker (worker portal).
    // SP/manager viewing a worker's quick view (worker=true) can still click to add information.
    const isClickable = hasPermission && !isWorkerUser;
    
    return (
      <>
        {roleAccess([USER_ROLE.Sp_Standard_User]) ? (
          <span className='text-xs text-red-500'>No Data found!</span>
        ) : (
          <span
            onClick={() => {
              if (isClickable) {
                handleTabs();
              }
            }}
            className={`${
              isClickable
                ? "cursor-pointer text-blue-600 hover:text-blue-700 flex items-center gap-1 text-xs font-medium"
                : "text-xs text-gray-400 flex items-center gap-1"
            }`}
          >
            Add information
            <FiArrowUpRight className="h-3 w-3 flex-shrink-0 inline-block" style={{ color: 'currentColor' }} />
          </span>
        )}
      </>
    );
  };

  const InfoCard = ({ icon: Icon, label, value, onClick, colSpan = "" }) => {
    const hasValue = value && value !== "No Data Found!" && value !== "Add information";
    const isClickable = onClick && !hasValue;
    return (
      <div
        className={`bg-white rounded-lg p-3 border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all ${colSpan} ${
          isClickable ? "cursor-pointer" : ""
        }`}
        onClick={isClickable ? onClick : undefined}
      >
        <div className="flex items-start space-x-2.5">
          {Icon && (
            <div className="flex-shrink-0 mt-0.5">
              <div className="h-7 w-7 rounded-lg bg-blue-50 flex items-center justify-center">
                <Icon className="h-3.5 w-3.5 text-blue-600" />
              </div>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <dt className="text-[10px] font-semibold text-gray-500 tracking-wide mb-1">
              {label}
            </dt>
            <dd className="text-xs font-medium text-gray-900 break-words leading-snug">
              {hasValue ? value : getValue(label)}
            </dd>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='min-h-full'>
      <div className={`${isQuickViewCall ? "sm:px-0" : ""} w-full`}>
        <div className='bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col max-h-[70vh] custom-scroll'>
          {/* Blue Background Header Section */}
          <div className='bg-gradient px-4 sm:px-5 py-3.5 sm:py-4 flex-shrink-0'>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'>
              <div>
                <h2 className='text-lg sm:text-xl font-bold text-white'>
                  Quick Snapshot
                </h2>
                <p className='text-xs text-blue-100 mt-0.5'>
                  {worker
                    ? 'Essential worker information'
                    : isCareLabelsLoading
                      ? <CareLabelSkeleton width="w-40" height="h-3" className="bg-blue-200/50" />
                      : `Essential ${participantLabel.toLowerCase()} information`}
                </p>
              </div>
              {roleAccess(
                [],
                PERMISSION_KEYS.PARTICIPANT,
                PERMISSION_KEYS_CHILDREN.PARTICIPANT.SETTING.SHOW
              ) &&
                roleAccess(
                  [],
                  PERMISSION_KEYS.PARTICIPANT,
                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.DETAIL
                ) && (
                  <button
                    onClick={onClickHandler}
                    className='flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white text-xs font-medium rounded-lg transition-all backdrop-blur-sm self-start sm:self-auto'
                  >
                    <span>Add information</span>
                    <FiArrowUpRight className="h-3.5 w-3.5" />
                  </button>
                )}
            </div>
          </div>

          <div className='overflow-y-scroll flex-1 min-h-0'>
          {isLoading ? (
            <div className='flex justify-center items-center py-12'>
              <CircularLoader
                classes='flex justify-center items-center'
                classloader='loader-l'
              />
            </div>
          ) : (
            <div className='p-3 sm:p-4'>
              {/* Quick Support/Snapshot Text - Compact */}
              {settingsData &&
              (settingsData.quick_support || settingsData.quick_snapshot) ? (
                <div className='mb-4 p-3 bg-gradient rounded-lg border border-blue-100'>
                  <p className='text-xs text-white leading-relaxed'>
                    {settingsData.quick_support
                      ? settingsData.quick_support
                      : settingsData.quick_snapshot}
                  </p>
                </div>
              ) : null}

              {/* Compact Information Grid - 3 columns on desktop, 2 on tablet, 1 on mobile */}
              <dl className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3'>
                {/* Address */}
                <InfoCard
                  icon={FiMapPin}
                  label="Address"
                  value={formatAddress(settingsData)}
                  onClick={onClickHandler}
                />

                {/* Primary Disability / Job Title */}
                <InfoCard
                  icon={FiUser}
                  label={worker ? "Job Title" : "Primary Disability"}
                  value={
                    settingsData?.primary_disability || settingsData?.job_title
                      ? settingsData.primary_disability || settingsData.job_title
                      : null
                  }
                  onClick={onClickHandler}
                />

                {/* Emergency Contact Name */}
                {roleAccess(
                  [],
                  PERMISSION_KEYS.PARTICIPANT,
                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.EMERGENCY_CONTACT_NAME
                ) && (
                  <InfoCard
                    icon={FiUser}
                    label="Emergency Contact Name"
                    value={settingsData?.emergency_contact_name || null}
                    onClick={onClickHandler}
                  />
                )}

                {/* Emergency Phone Number */}
                {roleAccess(
                  [],
                  PERMISSION_KEYS.PARTICIPANT,
                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.EMERGENCY_CONTACT_PHONE
                ) && (
                  <InfoCard
                    icon={FiPhone}
                    label="Emergency Phone Number"
                    value={
                      settingsData?.emergency_contact ||
                      settingsData?.emergency_contact_phone_number ||
                      null
                    }
                    onClick={onClickHandler}
                  />
                )}

                {/* Participant Phone */}
                {roleAccess(
                  [],
                  PERMISSION_KEYS.PARTICIPANT,
                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_PHONE
                ) &&
                  participantsInfo?.primary_no && (
                    <InfoCard
                      icon={FiPhone}
                      label={
                        isCareLabelsLoading ? (
                          <CareLabelSkeleton width="w-24" height="h-3" />
                        ) : (
                          `${participantLabel} Phone`
                        )
                      }
                      value={participantsInfo.primary_no || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* Joining Date */}
                {settingsData?.joining_date && (
                  <InfoCard
                    icon={FiCalendar}
                    label="Joining Date"
                    value={
                      settingsData?.joining_date
                        ? formatDateIntoDMYToDisplay(settingsData.joining_date)
                        : null
                    }
                    onClick={onClickHandler}
                  />
                )}

                {/* Participant Email */}
                {!worker &&
                  roleAccess(
                    [],
                    PERMISSION_KEYS.PARTICIPANT,
                    PERMISSION_KEYS_CHILDREN.PARTICIPANT.PRIMARY_EMAIL
                  ) && (
                    <InfoCard
                      icon={FiMail}
                      label={
                        isCareLabelsLoading ? (
                          <CareLabelSkeleton width="w-24" height="h-3" />
                        ) : (
                          `${participantLabel} Email`
                        )
                      }
                      value={participantsInfo?.email || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* NDIS No. */}
                {!worker &&
                  roleAccess(
                    [],
                    PERMISSION_KEYS.PARTICIPANT,
                    PERMISSION_KEYS_CHILDREN.PARTICIPANT.NDIS_NUMBER
                  ) && (
                    <InfoCard
                      icon={FiShield}
                      label="NDIS No."
                      value={settingsData?.ndis_no || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* Date of Birth */}
                {roleAccess(
                  [],
                  PERMISSION_KEYS.PARTICIPANT,
                  PERMISSION_KEYS_CHILDREN.PARTICIPANT.DOB
                ) && (
                  <InfoCard
                    icon={FiCalendar}
                    label="Date of Birth"
                    value={
                      settingsData?.dob
                        ? formatDateIntoDMYToDisplay(settingsData.dob)
                        : null
                    }
                    onClick={onClickHandler}
                  />
                )}

                {/* Service Start Date */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiCalendar}
                      label="Service Start Date"
                      value={
                        settingsData?.plan_start_date
                          ? formatDateIntoDMYToDisplay(settingsData.plan_start_date)
                          : null
                      }
                      onClick={onClickHandler}
                    />
                  )}

                {/* Service End Date */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiCalendar}
                      label="Service End Date"
                      value={
                        settingsData?.plan_end_date
                          ? formatDateIntoDMYToDisplay(settingsData.plan_end_date)
                          : null
                      }
                      onClick={onClickHandler}
                    />
                  )}

                {/* Public Guardian */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiUser}
                      label="Public Guardian"
                      value={settingsData?.public_guardian || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* Child Safety Officer */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiShield}
                      label="Child Safety Officer"
                      value={settingsData?.child_safety_officer || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* Plan Nominee */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiUser}
                      label="Plan Nominee"
                      value={settingsData?.plan_nominee || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* External Coordinator Name */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiUser}
                      label="External Coordinator Name"
                      value={settingsData?.external_coordinator_name || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* External Coordinator Email */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiMail}
                      label="External Coordinator Email"
                      value={settingsData?.external_coordinator_email || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* External Coordinator Phone */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiPhone}
                      label="External Coordinator Phone"
                      value={settingsData?.external_coordinator_phone || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* External Coordinator Company */}
                {!worker &&
                  roleAccess([], PERMISSION_KEYS.PARTICIPANT) && (
                    <InfoCard
                      icon={FiShield}
                      label="External Coordinator Company"
                      value={settingsData?.external_coordinator_company || null}
                      onClick={onClickHandler}
                    />
                  )}

                {/* Plan Manager Fields */}
                {settingsData &&
                  settingsData?.plan_managed_by != null &&
                  isPlanManagedByPlanManager(settingsData.plan_managed_by) && (
                    <>
                      <InfoCard
                        icon={FiUser}
                        label="Plan Manager Name"
                        value={settingsData?.plan_manager_name || null}
                        onClick={onClickHandler}
                      />
                      <InfoCard
                        icon={FiMail}
                        label="Plan Manager Email"
                        value={settingsData?.plan_manager_email || null}
                        onClick={onClickHandler}
                      />
                      <InfoCard
                        icon={FiShield}
                        label="Plan Manager Company"
                        value={settingsData?.plan_manager_company || null}
                        onClick={onClickHandler}
                      />
                    </>
                  )}
              </dl>
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickSnapshots;
